'use client';

import React from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import { useCountry } from '@/context/CountryContext';
import { useLanguage } from '@/context/LanguageContext';
import type { RequisitionStage } from '@/lib/mock-data';

export default function DashboardRequisitionsTable() {
  const { requisitions } = useCountry();
  const { t, fmtDate } = useLanguage();
  const recent = requisitions.slice(0, 5);

  function getStageLabel(stage: RequisitionStage): string {
    const shortKeys: Record<RequisitionStage, string> = {
      consultant_review: 'stage.short.consultant_review',
      signed_hardcopy: 'stage.short.signed_hardcopy',
      chief_director_approval: 'stage.short.chief_director_approval',
      main_stock_release: 'stage.short.main_stock_release',
    };
    return t(shortKeys[stage]);
  }

  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full text-sm">
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            {[
              t('table.requisitionId'),
              t('table.contractor'),
              t('table.stage'),
              t('table.priority'),
              t('table.status'),
            ].map((col) => (
              <th
                key={`dash-col-${col}`}
                className="text-left py-2 px-2 text-xs font-semibold uppercase tracking-wide text-text-muted"
                style={{ letterSpacing: '0.06em' }}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {recent.map((req, i) => (
            <tr
              key={req.id}
              className="transition-colors hover:bg-surface-light"
              style={{
                borderBottom: i < recent.length - 1 ? '1px solid var(--border)' : 'none',
              }}
            >
              <td className="py-2.5 px-2">
                <span
                  className="font-mono text-xs font-semibold"
                  style={{ color: 'var(--primary)' }}
                >
                  {req.moenId}
                </span>
              </td>
              <td className="py-2.5 px-2 text-text-main font-medium max-w-[160px] truncate">
                {req.contractor}
              </td>
              <td className="py-2.5 px-2 text-text-muted text-xs whitespace-nowrap">
                {getStageLabel(req.stage)}
              </td>
              <td className="py-2.5 px-2">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                    req.priority === 'urgent' ? 'bg-[color:var(--status-red)]/10 text-status-red'
                      : req.priority === 'high' ? 'bg-[color:var(--status-amber)]/10 text-status-amber' : 'bg-muted text-text-muted'
                  }`}
                >
                  {t(`priority.${req.priority}`)}
                </span>
              </td>
              <td className="py-2.5 px-2">
                <StatusBadge status={req.status} label={t(`status.${req.status}`)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}